<template>
    <div v-if="workflowStateIcon.icon" class="promotion-workflow-icon">
        <v-menu
            v-model="isMenuOpen"
            location="bottom"
            :close-on-content-click="true"
            :disabled="isActionsDisabled"
        >
            <template v-slot:activator="{ props: menuProps }">
                <v-tooltip location="top" :disabled="isMenuOpen">
                    <template v-slot:activator="{ props: tooltipProps }">
                        <div v-bind="{ ...menuProps, ...tooltipProps }">
                            <icon :icon-name="workflowStateIcon.icon" medium color="black" />
                        </div>
                    </template>
                    {{ tooltipText }}
                </v-tooltip>
            </template>
            <v-list v-if="actionableSteps.length" density="compact">
                <v-list-item
                    v-for="(item, idx) in actionableSteps"
                    :key="`${item.step.task}::${item.isNegativeAction}::${idx}`"
                    density="compact"
                    class="promotion-workflow-icon__task"
                    @click="runTask(item)"
                >
                    <v-btn
                        :prepend-icon="getTaskIcon(item)"
                        variant="text"
                        size="small"
                        density="compact"
                        class="promotion-workflow-icon__task-btn"
                    >
                        {{ getTaskLabel(item) }}
                    </v-btn>
                </v-list-item>
            </v-list>
        </v-menu>
    </div>
</template>

<script>
import { intersection, minBy } from 'lodash';
import { mapState, mapGetters } from 'vuex';
import store from '@/js/store';
import workflowEntities from '@enums/workflow-entities';
import workflowUtilsFactory from '@sharedModules/workflow-utils-factory';
import createFeatureAwareFactory from '@/js/feature-toggles/feature-factory';
import {
    hasWorkflowStepStarted,
    mapWorkflowStateIcons,
    userHasTaskPermission,
} from '@/js/utils/workflow-utils';
import promotionUtils from '@sharedModules/promotion-utils';
import lockedReasons from '@enums/locked-reasons';

const workflowUtils = workflowUtilsFactory();

export default {
    props: {
        workflowState: {
            type: Array,
            default: () => [],
        },
        promotionId: {
            type: String,
            required: false,
            default: undefined,
        },
        subCampaign: {
            type: Object,
            required: false,
            default: null,
        },
        disabled: {
            type: Boolean,
            required: false,
            default: false,
        },
    },
    emits: ['action-completed'],
    data() {
        return {
            isMenuOpen: false,
            workflowEntities,
        };
    },
    computed: {
        ...mapState('clientConfig', ['toggleLogic']),
        ...mapState('promotions', ['unsavedPromotion']),
        ...mapGetters('promotions', ['getPromotionById', 'pendingChanges']),
        ...mapGetters('context', ['userRoles']),
        promotion() {
            return this.promotionId ? this.getPromotionById(this.promotionId) : null;
        },
        isPromotionUnsaved() {
            const tabValues = Object.values(this.unsavedPromotion[this.promotionId] || {});
            return tabValues.filter(v => v).length > 0;
        },
        isPastPromotion() {
            return (
                !!this.promotion &&
                promotionUtils.isPromotionPastEndDate({
                    moment: this.$moment,
                    endDate: this.promotion.endDate,
                })
            );
        },
        hasChangesToReview() {
            return this.pendingChanges({
                promotionId: this.promotionId,
            });
        },
        isActionsDisabled() {
            return this.disabled || this.isPastPromotion;
        },
        actionsDisabledReason() {
            if (this.isPromotionUnsaved) {
                return this.toSentenceCase(this.$t(lockedReasons.promotionUnSaved));
            }

            if (this.promotion && this.promotion.splitPromotion) {
                return this.toSentenceCase(this.$t(lockedReasons.splitPromotion));
            }

            if (this.isPastPromotion) {
                return this.toSentenceCase(this.$t(lockedReasons.promotionPastEndDate));
            }

            if (this.hasChangesToReview) {
                return this.toSentenceCase(this.$t(lockedReasons.promotionNotificationExist));
            }

            return null;
        },
        categories() {
            return (this.promotion && this.promotion.userSelectedCategories) || [];
        },
        isStoreWidePromotion() {
            return this.promotion ? promotionUtils.isStoreWidePromotion(this.promotion) : false;
        },
        workflowStateIconsMap() {
            try {
                return createFeatureAwareFactory(this.toggleLogic).getPromotionStateIconsMap();
            } catch {
                return {
                    iconsStateMap: {},
                    iconsStateMapOrdering: [],
                    iconsStateMapToReleaseFlag: {},
                };
            }
        },
        workflowStateSCAPiconsMap() {
            try {
                return createFeatureAwareFactory(
                    this.toggleLogic
                ).getSubCampaignAllocationWorkflowStateIconsMap();
            } catch {
                return {
                    iconsStateMap: {},
                    iconsStateMapOrdering: [],
                    iconsStateMapToReleaseFlag: {},
                };
            }
        },
        workflowStateIcon() {
            const workflowStateIcon = mapWorkflowStateIcons({
                workflowState: this.workflowState,
                ...this.workflowStateIconsMap,
            });

            if (workflowStateIcon.icon) return workflowStateIcon;

            return mapWorkflowStateIcons({
                workflowState: this.workflowState,
                ...this.workflowStateSCAPiconsMap,
            });
        },
        tooltipText() {
            const text = this.workflowStateIcon.text;
            const stateText = text
                ? this.toSentenceCase(this.$t(`workflow.taskButton.states.${text}`))
                : '';

            if (this.isActionsDisabled && this.actionsDisabledReason) {
                return stateText
                    ? `${stateText} - ${this.actionsDisabledReason}`
                    : this.actionsDisabledReason;
            }

            return stateText;
        },
        actionableSteps() {
            const workflow = this.subCampaign && this.subCampaign.workflow;

            if (!workflow || !workflow.steps) return [];

            const mapCategories = this.categories.map(cat =>
                typeof cat === 'object' ? cat.levelEntryKey : cat
            );

            const taskGroups = {};
            workflow.steps
                .filter(step => {
                    if (step.entity !== workflowEntities.promotion) return false;
                    if (step.categories && !this.isStoreWidePromotion) {
                        if (!intersection(step.categories, mapCategories).length) return false;
                    }
                    return true;
                })
                .forEach(step => {
                    if (!taskGroups[step.task]) taskGroups[step.task] = [];
                    taskGroups[step.task].push(step);
                });

            const items = [];
            Object.values(taskGroups)
                .map(steps => minBy(steps, 'deadlineDateTime'))
                .filter(step =>
                    workflowUtils.canStepBeCompleted({
                        workflowState: this.workflowState,
                        step,
                        getParentWorkflowState: () => [],
                    })
                )
                .filter(step => hasWorkflowStepStarted({ step, moment: this.$moment }))
                .forEach(step => {
                    const taskDef = workflowUtils.taskDefinitions[step.entity]?.[step.task];
                    if (!taskDef) return;

                    // Only offer tasks the logged-in user has a role for.
                    const hasTaskPermission = userHasTaskPermission({
                        taskDefinition: taskDef,
                        userRoles: this.userRoles,
                    });
                    if (!hasTaskPermission) return;

                    items.push({ step, isNegativeAction: false, taskDef });

                    if (taskDef.negativeButtonText) {
                        items.push({ step, isNegativeAction: true, taskDef });
                    }
                });
            return items;
        },
    },
    methods: {
        getTaskIcon({ isNegativeAction, taskDef }) {
            const taskIcon = isNegativeAction ? taskDef.negativeIcon : taskDef.positiveIcon;
            return taskIcon ? `custom:${taskIcon}` : undefined;
        },
        getTaskLabel(item) {
            const { isNegativeAction, taskDef } = item;
            const textKey = isNegativeAction ? taskDef.negativeButtonText : taskDef.buttonText;
            return this.toSentenceCase(this.$t(`workflow.taskButton.tasks.${textKey}.short`));
        },
        async runTask(item) {
            await store.dispatch('promotions/runTask', {
                task: item.step.task,
                isNegativeAction: item.isNegativeAction,
                workflowEntity: workflowEntities.promotion,
                entityIds: [this.promotionId],
            });
            this.isMenuOpen = false;
            this.$emit('action-completed');
        },
    },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

.promotion-workflow-icon {
    &__task {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0.3rem 0;
        cursor: pointer;

        &:hover {
            background: $promo-light-blue-2;
        }
    }

    &__task-btn {
        background-color: transparent !important;
    }

    &__task-btn:before {
        opacity: 0 !important;
    }
}
</style>
